import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { 
  ArrowLeft, Plus, Trash2, Save, Edit2, Euro, Percent, 
  ChevronDown, ChevronUp, GripVertical
} from 'lucide-react';
import { formatCurrency } from '../lib/utils';

const PaymentSettingsPage = () => {
  const navigate = useNavigate();
  const { isAdmin, isAdministration } = useAuth();
  
  const [tiers, setTiers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  const [showTierDialog, setShowTierDialog] = useState(false);
  const [editingTier, setEditingTier] = useState(null);
  const [tierForm, setTierForm] = useState({
    name: '',
    min_amount: 0,
    max_amount: '',
    milestones: []
  });
  
  const [expandedTiers, setExpandedTiers] = useState({});
  
  const triggerTypeLabels = {
    'firma_contratto': 'Firma contratto',
    'inizio_lavori': 'Inizio lavori',
    'sal_percentuale': 'SAL %',
    'fine_lavori': 'Fine lavori'
  };

  useEffect(() => {
    loadTiers();
  }, []);

  const loadTiers = async () => {
    try {
      const response = await fetch(`/api/payment-tiers`, {
        withCredentials: true
      });
      if (response.ok) {
        const data = await response.json();
        setTiers(data);
        // Espandi tutte le fasce di default
        const expanded = {};
        data.forEach(t => expanded[t.id] = true);
        setExpandedTiers(expanded);
      }
    } catch (error) {
      toast.error('Errore nel caricamento delle fasce');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenTierDialog = (tier = null) => {
    if (tier) {
      setEditingTier(tier);
      setTierForm({
        name: tier.name,
        min_amount: tier.min_amount,
        max_amount: tier.max_amount || '',
        milestones: tier.milestones || []
      });
    } else {
      setEditingTier(null);
      setTierForm({
        name: '',
        min_amount: 0,
        max_amount: '',
        milestones: [
          { name: 'Firma contratto', percentage: 10, trigger_type: 'firma_contratto', trigger_sal_percentage: null, order: 1 }
        ]
      });
    }
    setShowTierDialog(true);
  };

  const handleAddMilestone = () => {
    const newOrder = tierForm.milestones.length + 1;
    setTierForm(prev => ({
      ...prev,
      milestones: [
        ...prev.milestones,
        { name: `SAL ${newOrder - 1}`, percentage: 10, trigger_type: 'sal_percentuale', trigger_sal_percentage: 50, order: newOrder }
      ]
    }));
  };

  const handleRemoveMilestone = (index) => {
    setTierForm(prev => ({
      ...prev,
      milestones: prev.milestones.filter((_, i) => i !== index).map((m, i) => ({ ...m, order: i + 1 }))
    }));
  };

  const handleUpdateMilestone = (index, field, value) => {
    setTierForm(prev => ({
      ...prev,
      milestones: prev.milestones.map((m, i) => i === index ? { ...m, [field]: value } : m)
    }));
  };

  const handleSaveTier = async () => {
    // Validazione
    if (!tierForm.name.trim()) {
      toast.error('Inserisci il nome della fascia');
      return;
    }
    
    const totalPercentage = tierForm.milestones.reduce((sum, m) => sum + (parseFloat(m.percentage) || 0), 0);
    if (Math.abs(totalPercentage - 100) > 0.01) {
      toast.error(`Il totale delle percentuali deve essere 100% (attuale: ${totalPercentage}%)`);
      return;
    }
    
    setSaving(true);
    try {
      const payload = {
        ...tierForm,
        min_amount: parseFloat(tierForm.min_amount) || 0,
        max_amount: tierForm.max_amount ? parseFloat(tierForm.max_amount) : null,
        milestones: tierForm.milestones.map(m => ({
          ...m,
          percentage: parseFloat(m.percentage) || 0,
          trigger_sal_percentage: m.trigger_type === 'sal_percentuale' ? (parseFloat(m.trigger_sal_percentage) || 0) : null
        }))
      };
      
      const url = editingTier 
        ? `/api/payment-tiers/${editingTier.id}`
        : `/api/payment-tiers`;
      
      const response = await fetch(url, {
        method: editingTier ? 'PUT' : 'POST',
        headers: {
          withCredentials: true,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });
      
      if (response.ok) {
        toast.success(editingTier ? 'Fascia aggiornata' : 'Fascia creata');
        setShowTierDialog(false);
        loadTiers();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Errore nel salvataggio');
      }
    } catch (error) {
      toast.error('Errore nel salvataggio');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteTier = async (tierId) => {
    if (!window.confirm('Sei sicuro di voler eliminare questa fascia?')) return;
    
    try {
      const response = await fetch(`/api/payment-tiers/${tierId}`, {
        method: 'DELETE',
        withCredentials: true
      });
      
      if (response.ok) {
        toast.success('Fascia eliminata');
        loadTiers();
      }
    } catch (error) {
      toast.error('Errore nell\'eliminazione');
    }
  };

  const toggleTierExpand = (tierId) => {
    setExpandedTiers(prev => ({ ...prev, [tierId]: !prev[tierId] }));
  };

  if (!isAdmin && !isAdministration) {
    return (
      <div className="p-8 text-center">
        <p className="text-[#737373]">Accesso non autorizzato</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="p-8 text-center">
        <p className="text-[#737373]">Caricamento...</p>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-8 space-y-6" data-testid="payment-settings-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" onClick={() => navigate('/admin')}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Indietro
          </Button>
          <div>
            <h1 className="text-h3 font-bold">Impostazioni Pagamenti</h1>
            <p className="text-sm text-[#737373]">Configura le fasce e le milestone di pagamento</p>
          </div>
        </div>
        <Button className="btn-primary" onClick={() => handleOpenTierDialog()}>
          <Plus className="w-4 h-4 mr-2" />
          Nuova Fascia
        </Button>
      </div>

      {/* Fasce */}
      <div className="space-y-4">
        {tiers.map(tier => {
          const isExpanded = expandedTiers[tier.id];
          const totalPercentage = tier.milestones?.reduce((sum, m) => sum + m.percentage, 0) || 0;
          
          return (
            <Card key={tier.id} className="card-abitalo">
              <CardHeader 
                className="cursor-pointer hover:bg-[#FAFAFA] transition-colors"
                onClick={() => toggleTierExpand(tier.id)}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    {isExpanded ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
                    <div>
                      <CardTitle className="text-lg">{tier.name}</CardTitle>
                      <p className="text-sm text-[#737373]">
                        {formatCurrency(tier.min_amount)} - {tier.max_amount ? formatCurrency(tier.max_amount) : '∞'}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge variant="outline">{tier.milestones?.length || 0} milestone</Badge>
                    <Badge className={totalPercentage === 100 ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}>
                      {totalPercentage}%
                    </Badge>
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      onClick={(e) => { e.stopPropagation(); handleOpenTierDialog(tier); }}
                    >
                      <Edit2 className="w-4 h-4" />
                    </Button>
                    <Button 
                      variant="ghost" 
                      size="sm"
                      className="text-red-500 hover:text-red-700"
                      onClick={(e) => { e.stopPropagation(); handleDeleteTier(tier.id); }}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </CardHeader>
              
              {isExpanded && (
                <CardContent className="pt-0">
                  <div className="border rounded-lg overflow-hidden">
                    <table className="w-full">
                      <thead className="bg-[#F5F5F5]">
                        <tr>
                          <th className="px-4 py-2 text-left text-xs font-medium text-[#737373]">#</th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-[#737373]">Milestone</th>
                          <th className="px-4 py-2 text-center text-xs font-medium text-[#737373]">%</th>
                          <th className="px-4 py-2 text-center text-xs font-medium text-[#737373]">Cumulativo</th>
                          <th className="px-4 py-2 text-left text-xs font-medium text-[#737373]">Condizione</th>
                          <th className="px-4 py-2 text-center text-xs font-medium text-[#737373]">Fattura quando SAL ≥</th>
                        </tr>
                      </thead>
                      <tbody>
                        {tier.milestones?.sort((a, b) => a.order - b.order).map((milestone, index) => {
                          const cumulative = tier.milestones
                            .filter(m => m.order <= milestone.order)
                            .reduce((sum, m) => sum + m.percentage, 0);
                          
                          return (
                            <tr key={milestone.id || `milestone-${tier.id}-${milestone.order}`} className="border-t border-[#F2F2F2]">
                              <td className="px-4 py-3 text-sm">{milestone.order}</td>
                              <td className="px-4 py-3 text-sm font-medium">{milestone.name}</td>
                              <td className="px-4 py-3 text-sm text-center">
                                <span className="font-semibold text-[#FF7819]">{milestone.percentage}%</span>
                              </td>
                              <td className="px-4 py-3 text-sm text-center text-[#737373]">{cumulative}%</td>
                              <td className="px-4 py-3 text-sm">
                                <Badge variant="outline">{triggerTypeLabels[milestone.trigger_type]}</Badge>
                              </td>
                              <td className="px-4 py-3 text-sm text-center">
                                {milestone.trigger_sal_percentage !== null ? (
                                  <span className="font-medium text-[#0078C8]">{milestone.trigger_sal_percentage}%</span>
                                ) : (
                                  <span className="text-[#999]">-</span>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </CardContent>
              )}
            </Card>
          );
        })}
      </div>

      {/* Tier Dialog */}
      <Dialog open={showTierDialog} onOpenChange={setShowTierDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingTier ? 'Modifica Fascia' : 'Nuova Fascia'}</DialogTitle>
            <DialogDescription>
              Configura la fascia di importo e le relative milestone di pagamento
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-6 py-4">
            {/* Tier Info */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="md:col-span-3">
                <Label>Nome Fascia</Label>
                <Input
                  value={tierForm.name}
                  onChange={(e) => setTierForm(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="Es: Fascia 15.000€ - 50.000€"
                />
              </div>
              <div>
                <Label>Importo Minimo (€)</Label>
                <Input
                  type="number"
                  value={tierForm.min_amount}
                  onChange={(e) => setTierForm(prev => ({ ...prev, min_amount: e.target.value }))}
                />
              </div>
              <div>
                <Label>Importo Massimo (€)</Label>
                <Input
                  type="number"
                  value={tierForm.max_amount}
                  onChange={(e) => setTierForm(prev => ({ ...prev, max_amount: e.target.value }))}
                  placeholder="Lascia vuoto per ∞"
                />
              </div>
              <div className="flex items-end">
                <div className="text-sm text-[#737373]">
                  Totale: <span className={tierForm.milestones.reduce((sum, m) => sum + (parseFloat(m.percentage) || 0), 0) === 100 ? 'text-green-600 font-bold' : 'text-red-600 font-bold'}>
                    {tierForm.milestones.reduce((sum, m) => sum + (parseFloat(m.percentage) || 0), 0)}%
                  </span>
                </div>
              </div>
            </div>
            
            {/* Milestones */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <Label>Milestone</Label>
                <Button variant="outline" size="sm" onClick={handleAddMilestone}>
                  <Plus className="w-4 h-4 mr-1" />
                  Aggiungi
                </Button>
              </div>
              
              <div className="space-y-3">
                {tierForm.milestones.map((milestone, index) => {
                  const cumulative = tierForm.milestones
                    .slice(0, index + 1)
                    .reduce((sum, m) => sum + (parseFloat(m.percentage) || 0), 0);
                  
                  return (
                    <div key={milestone.id || `form-milestone-${index}`} className="flex items-center gap-3 p-3 bg-[#FAFAFA] rounded-lg">
                      <GripVertical className="w-4 h-4 text-[#999]" />
                      <span className="text-sm font-medium w-6">{index + 1}.</span>
                      
                      <Input
                        value={milestone.name}
                        onChange={(e) => handleUpdateMilestone(index, 'name', e.target.value)}
                        className="w-40"
                        placeholder="Nome"
                      />
                      
                      <div className="flex items-center gap-1">
                        <Input
                          type="number"
                          value={milestone.percentage}
                          onChange={(e) => handleUpdateMilestone(index, 'percentage', e.target.value)}
                          className="w-20"
                        />
                        <Percent className="w-4 h-4 text-[#737373]" />
                      </div>
                      
                      <Select
                        value={milestone.trigger_type}
                        onValueChange={(v) => handleUpdateMilestone(index, 'trigger_type', v)}
                      >
                        <SelectTrigger className="w-40">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="firma_contratto">Firma contratto</SelectItem>
                          <SelectItem value="inizio_lavori">Inizio lavori</SelectItem>
                          <SelectItem value="sal_percentuale">SAL %</SelectItem>
                          <SelectItem value="fine_lavori">Fine lavori</SelectItem>
                        </SelectContent>
                      </Select>
                      
                      {milestone.trigger_type === 'sal_percentuale' && (
                        <div className="flex items-center gap-1">
                          <span className="text-xs text-[#737373]">SAL ≥</span>
                          <Input
                            type="number"
                            value={milestone.trigger_sal_percentage || ''}
                            onChange={(e) => handleUpdateMilestone(index, 'trigger_sal_percentage', e.target.value)}
                            className="w-16"
                          />
                          <Percent className="w-3 h-3 text-[#737373]" />
                        </div>
                      )}
                      
                      <span className="text-xs text-[#999]">Cum: {cumulative}%</span>
                      
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-red-500"
                        onClick={() => handleRemoveMilestone(index)}
                        disabled={tierForm.milestones.length <= 1}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowTierDialog(false)}>
              Annulla
            </Button>
            <Button className="btn-primary" onClick={handleSaveTier} disabled={saving}>
              <Save className="w-4 h-4 mr-2" />
              {saving ? 'Salvataggio...' : 'Salva'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PaymentSettingsPage;
